import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyContext } from './use-company-context';

export interface CostData {
  category: string;
  amount: number;
  percentage: number;
  change: number;
  count: number;
}

export interface VehicleCost {
  vehicle_id: string;
  plate: string;
  make?: string;
  model?: string;
  total_cost: number;
  fuel_cost: number;
  maintenance_cost: number;
  insurance_cost: number;
  other_cost: number;
}

export function useFleetCosts(companyIdParam?: string, period: string = 'month', year: string = '2024') {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useFleetCosts] companyId:', companyId, 'period:', period, 'year:', year);
  
  return useQuery({
    queryKey: ['fleet-costs', companyId, period, year],
    queryFn: async () => {
      if (!companyId) return { categories: [], vehicles: [] };

      // Calculate date range based on period and selected year
      const now = new Date();
      const selectedYear = parseInt(year);
      let startDate: Date;
      let endDate: Date;
      
      if (period === 'month') {
        // Current month of selected year
        startDate = new Date(selectedYear, now.getMonth(), 1);
        endDate = new Date(selectedYear, now.getMonth() + 1, 1);
      } else if (period === 'quarter') {
        // Current quarter of selected year
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(selectedYear, quarter * 3, 1);
        endDate = new Date(selectedYear, (quarter + 1) * 3, 1);
      } else {
        // Full year
        startDate = new Date(selectedYear, 0, 1);
        endDate = new Date(selectedYear + 1, 0, 1);
      }

      console.log('📅 [useFleetCosts] Date range:', { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString(),
        period,
        year: selectedYear
      });

      // Get fuel costs
      const { data: fuelLogs, error: fuelError } = await supabase
        .from('fleet_fuel_logs')
        .select(`
          *,
          fleet_vehicles!inner (
            id,
            plate,
            make,
            model
          )
        `)
        .eq('company_id', companyId)
        .gte('occurred_at', startDate.toISOString())
        .lt('occurred_at', endDate.toISOString());

      if (fuelError) throw fuelError;
      console.log(`🔥 [useFleetCosts] Fuel logs found: ${fuelLogs?.length || 0}`);

      // Get maintenance costs (work orders)
      const { data: workOrders, error: workOrdersError } = await supabase
        .from('fleet_work_orders')
        .select(`
          *,
          fleet_vehicles!inner (
            id,
            plate,
            make,
            model
          )
        `)
        .eq('company_id', companyId)
        .gte('opened_at', startDate.toISOString())
        .lt('opened_at', endDate.toISOString());

      if (workOrdersError) throw workOrdersError;
      console.log(`🔧 [useFleetCosts] Work orders found: ${workOrders?.length || 0}`);

      // Calculate fuel costs by vehicle
      const fuelCostsByVehicle = fuelLogs?.reduce((acc: Record<string, {
        vehicle_id: string;
        plate: string;
        make?: string;
        model?: string;
        fuel_cost: number;
        fuel_count: number;
      }>, log) => {
        const vehicleId = log.fleet_vehicles.id;
        const cost = Number(log.liters || 0) * Number(log.price_unit || 0);
        
        if (!acc[vehicleId]) {
          acc[vehicleId] = {
            vehicle_id: vehicleId,
            plate: log.fleet_vehicles.plate,
            make: log.fleet_vehicles.make,
            model: log.fleet_vehicles.model,
            fuel_cost: 0,
            fuel_count: 0,
          };
        }
        
        acc[vehicleId].fuel_cost += cost;
        acc[vehicleId].fuel_count += 1;
        
        return acc;
      }, {}) || {};

      // Calculate maintenance costs by vehicle
      const maintenanceCostsByVehicle = workOrders?.reduce((acc: Record<string, {
        vehicle_id: string;
        plate: string;
        make?: string;
        model?: string;
        maintenance_cost: number;
        maintenance_count: number;
      }>, order) => {
        const vehicleId = order.fleet_vehicles.id;
        const cost = Number(order.total_cost || 0);
        
        if (!acc[vehicleId]) {
          acc[vehicleId] = {
            vehicle_id: vehicleId,
            plate: order.fleet_vehicles.plate,
            make: order.fleet_vehicles.make,
            model: order.fleet_vehicles.model,
            maintenance_cost: 0,
            maintenance_count: 0,
          };
        }
        
        acc[vehicleId].maintenance_cost += cost;
        acc[vehicleId].maintenance_count += 1;
        
        return acc;
      }, {}) || {};

      // Combine all vehicle costs
      const allVehicleIds = new Set([
        ...Object.keys(fuelCostsByVehicle),
        ...Object.keys(maintenanceCostsByVehicle)
      ]);

      const vehicleCosts: VehicleCost[] = Array.from(allVehicleIds).map(vehicleId => {
        const fuel = fuelCostsByVehicle[vehicleId] || { fuel_cost: 0, plate: '', make: '', model: '' };
        const maintenance = maintenanceCostsByVehicle[vehicleId] || { maintenance_cost: 0, plate: '', make: '', model: '' };
        
        const plate = fuel.plate || maintenance.plate;
        const make = fuel.make || maintenance.make;
        const model = fuel.model || maintenance.model;
        
        return {
          vehicle_id: vehicleId,
          plate,
          make,
          model,
          fuel_cost: Number(fuel.fuel_cost),
          maintenance_cost: Number(maintenance.maintenance_cost),
          insurance_cost: 0, // TODO: Implement when insurance data is available
          other_cost: 0, // TODO: Implement when other costs data is available
          total_cost: Number(fuel.fuel_cost) + Number(maintenance.maintenance_cost),
        };
      });

      // Calculate category totals
      const totalFuelCost = Object.values(fuelCostsByVehicle).reduce((sum: number, v) => sum + v.fuel_cost, 0);
      const totalMaintenanceCost = Object.values(maintenanceCostsByVehicle).reduce((sum: number, v) => sum + v.maintenance_cost, 0);
      const totalCost = totalFuelCost + totalMaintenanceCost;

      const fuelCount = Object.values(fuelCostsByVehicle).reduce((sum: number, v) => sum + v.fuel_count, 0);
      const maintenanceCount = Object.values(maintenanceCostsByVehicle).reduce((sum: number, v) => sum + v.maintenance_count, 0);

      const categories: CostData[] = [
        {
          category: 'Combustível',
          amount: totalFuelCost,
          percentage: totalCost > 0 ? (totalFuelCost / totalCost) * 100 : 0,
          change: 0,
          count: fuelCount,
        },
        {
          category: 'Manutenção',
          amount: totalMaintenanceCost,
          percentage: totalCost > 0 ? (totalMaintenanceCost / totalCost) * 100 : 0,
          change: 0,
          count: maintenanceCount,
        },
      ].filter(cat => cat.amount > 0);

      return {
        categories,
        vehicles: vehicleCosts,
      };
    },
    enabled: !!companyId,
  });
}

export function useFleetCostsSummary(companyIdParam?: string) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useFleetCostsSummary] companyId:', companyId);
  
  return useQuery({
    queryKey: ['fleet-costs-summary', companyId],
    queryFn: async () => {
      if (!companyId) return null;

      // Get current month data
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // Get total fuel cost for current month
      const { data: fuelData, error: fuelError } = await supabase
        .from('fleet_fuel_logs')
        .select('liters, price_unit')
        .eq('company_id', companyId)
        .gte('occurred_at', startOfMonth.toISOString())
        .lte('occurred_at', endOfMonth.toISOString());

      if (fuelError) throw fuelError;

      // Get total maintenance cost for current month
      const { data: maintenanceData, error: maintenanceError } = await supabase
        .from('fleet_work_orders')
        .select('total_cost')
        .eq('company_id', companyId)
        .gte('opened_at', startOfMonth.toISOString())
        .lte('opened_at', endOfMonth.toISOString());

      if (maintenanceError) throw maintenanceError;

      const totalFuelCost = fuelData?.reduce((sum, log) => sum + (Number(log.liters || 0) * Number(log.price_unit || 0)), 0) || 0;
      const totalMaintenanceCost = maintenanceData?.reduce((sum, order) => sum + Number(order.total_cost || 0), 0) || 0;
      const totalCost = totalFuelCost + totalMaintenanceCost;

      return {
        totalCost,
        fuelCost: totalFuelCost,
        maintenanceCost: totalMaintenanceCost,
        monthlyAverage: totalCost, // For current month
        topCategory: totalFuelCost > totalMaintenanceCost ? 'Combustível' : 'Manutenção',
        trend: 0, // TODO: Calculate trend from previous month
      };
    },
    enabled: !!companyId,
  });
}